import { useState } from 'react';
import { api, type Anagrafica, type Categoria } from '../api';
import {
  Campo,
  Caricamento,
  Dialogo,
  Errore,
  Etichetta,
  Scheda,
  Vuoto,
  useAlberoCategorie,
  useAvvisi,
  useCarico,
  useDebounce,
  useFiltriUrl,
} from '../componenti/base';
import { plurale } from '../formato';

const TIPI = [
  { v: '', t: 'Tutti' },
  { v: 'CLIENTE', t: 'Clienti' },
  { v: 'FORNITORE', t: 'Fornitori' },
  { v: 'ENTRAMBI', t: 'Solo «entrambi»' },
];

export function Anagrafiche() {
  // Nell'indirizzo: uscire da un elenco filtrato e tornarci non deve far
  // ricominciare la ricerca da capo.
  const { filtri, imposta } = useFiltriUrl({ cerca: '', tipo: '' });
  const { cerca, tipo } = filtri;
  const [aperta, setAperta] = useState<Anagrafica | 'nuova' | null>(null);
  const cercaRitardata = useDebounce(cerca);

  const query = new URLSearchParams();
  if (cercaRitardata) query.set('cerca', cercaRitardata);
  if (tipo) query.set('tipo', tipo);

  const { dati, caricamento, errore, ricarica } = useCarico(
    () => api.get<Anagrafica[]>(`/api/anagrafiche?${query}`),
    [cercaRitardata, tipo],
  );
  const categorie = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);

  return (
    <div className="pagina space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Anagrafiche</h1>
          <p className="text-sm text-inchiostro-500">
            Clienti e fornitori. L’abbinamento con le fatture elettroniche passa dalla partita IVA — o dal codice
            fiscale, per società semplici, associazioni e persone fisiche che la P.IVA non ce l’hanno — mai dalla
            denominazione: «S.p.A.», «SpA» e «S.P.A.» sono la stessa azienda.
          </p>
        </div>
        <button className="bottone-primario" onClick={() => setAperta('nuova')}>
          Nuova anagrafica
        </button>
      </header>

      <RitardiOsservati onApplicato={ricarica} />

      <Scheda senzaPadding>
        <div className="flex flex-wrap items-end gap-3 border-b border-inchiostro-200 p-3">
          <Campo etichetta="Cerca" larghezza="w-72">
            <input
              className="campo"
              placeholder="Denominazione, P.IVA, codice fiscale o comune"
              value={cerca}
              onChange={(e) => imposta({ cerca: e.target.value })}
            />
          </Campo>
          <Campo etichetta="Tipo" larghezza="w-44">
            <select className="campo" value={tipo} onChange={(e) => imposta({ tipo: e.target.value })}>
              {TIPI.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.t}
                </option>
              ))}
            </select>
          </Campo>
          <span className="pb-2 text-sm text-inchiostro-500">{plurale(dati?.length ?? 0, 'risultato', 'risultati')}</span>
        </div>

        {caricamento ? (
          <Caricamento />
        ) : errore ? (
          <div className="p-4"><Errore testo={errore} riprova={ricarica} /></div>
        ) : (dati ?? []).length === 0 ? (
          <Vuoto titolo="Nessuna anagrafica" testo="Si creano a mano oppure da sole, importando una fattura elettronica." />
        ) : (
          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Denominazione</th>
                  <th>Partita IVA</th>
                  <th title="Società semplici, associazioni e persone fisiche spesso hanno solo questo.">
                    Codice fiscale
                  </th>
                  <th>Tipo</th>
                  <th>Comune</th>
                  <th className="text-right" title="La data di cassa attesa è la scadenza più questi giorni">
                    Ritardo medio
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dati!.map((a) => (
                  <tr key={a.id}>
                    <td className="font-medium">{a.denominazione}</td>
                    <td className="num">{a.partitaIva || <span className="text-inchiostro-400">—</span>}</td>
                    {/* Chi non ha partita IVA — società semplici, associazioni,
                        persone fisiche — è identificato dal codice fiscale, e
                        senza questa colonna la riga sembrava senza dati. */}
                    <td className="num">{a.codiceFiscale || <span className="text-inchiostro-400">—</span>}</td>
                    <td>
                      <Etichetta
                        testo={a.tipo === 'ENTRAMBI' ? 'cliente e fornitore' : a.tipo.toLowerCase()}
                        tono={a.tipo === 'ENTRAMBI' ? 'info' : 'neutro'}
                        titolo={
                          a.tipo === 'ENTRAMBI'
                            ? 'Nella subfornitura una controparte è spesso sia cliente sia fornitore.'
                            : undefined
                        }
                      />
                    </td>
                    <td>{a.comune ?? '—'}</td>
                    <td className="num text-right">
                      {/* Tre casi diversi, che prima si leggevano uguali: «puntuale»
                          misurato su molte fatture, «pochi dati», e «niente». Uno
                          zero con il suo campione è un'informazione, non un vuoto. */}
                      {!a.ritardoAutomatico || (a.fatturePerRitardo ?? 0) >= (a.minimoPerRitardo ?? 3) ? (
                        <span
                          title={
                            a.ritardoAutomatico
                              ? `Misurato su ${a.fatturePerRitardo} fatture pagate: mediana fra scadenza e incasso.`
                              : undefined
                          }
                        >
                          {a.ritardoMedioGiorni === 0
                            ? 'puntuale'
                            : `${a.ritardoMedioGiorni > 0 ? '+' : '\u2212'}${Math.abs(a.ritardoMedioGiorni)} gg`}
                          {a.ritardoAutomatico && (
                            <span className="ml-1 text-[10px] text-inchiostro-400">su {a.fatturePerRitardo}</span>
                          )}
                        </span>
                      ) : (a.fatturePerRitardo ?? 0) > 0 ? (
                        <span
                          className="text-xs text-inchiostro-400"
                          title={`${a.fatturePerRitardo} ${a.fatturePerRitardo === 1 ? 'fattura pagata' : 'fatture pagate'}: ne servono ${a.minimoPerRitardo ?? 3} per dire qualcosa. Fino ad allora vale la scadenza.`}
                        >
                          pochi dati
                        </span>
                      ) : (
                        <span className="text-inchiostro-400" title="Nessuna fattura pagata e abbinata: non c'è niente da misurare.">
                          —
                        </span>
                      )}
                      {!a.ritardoAutomatico && (
                        <span
                          className="ml-1.5 text-[10px] text-inchiostro-500"
                          title="Tenuto a mano: Mastro non lo aggiorna dai pagamenti osservati."
                        >
                          a mano
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <button className="text-xs underline underline-offset-2" onClick={() => setAperta(a)}>
                        Apri
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>

      {aperta && (
        <FormAnagrafica
          anagrafica={aperta === 'nuova' ? null : aperta}
          categorie={categorie.dati ?? []}
          onChiudi={() => setAperta(null)}
          onSalvata={() => {
            setAperta(null);
            ricarica();
          }}
        />
      )}
    </div>
  );
}

function FormAnagrafica({
  anagrafica,
  categorie,
  onChiudi,
  onSalvata,
}: {
  anagrafica: Anagrafica | null;
  categorie: Categoria[];
  onChiudi: () => void;
  onSalvata: () => void;
}) {
  const avvisa = useAvvisi();
  const { piatto } = useAlberoCategorie(categorie);
  const [v, setV] = useState({
    denominazione: anagrafica?.denominazione ?? '',
    partitaIva: anagrafica?.partitaIva ?? '',
    codiceFiscale: anagrafica?.codiceFiscale ?? '',
    tipo: anagrafica?.tipo ?? 'CLIENTE',
    indirizzo: anagrafica?.indirizzo ?? '',
    cap: anagrafica?.cap ?? '',
    comune: anagrafica?.comune ?? '',
    provincia: anagrafica?.provincia ?? '',
    pec: anagrafica?.pec ?? '',
    codiceDestinatario: anagrafica?.codiceDestinatario ?? '',
    categoriaId: anagrafica?.categoriaId ?? '',
    ritardoMedioGiorni: anagrafica?.ritardoMedioGiorni ?? 0,
    ritardoAutomatico: anagrafica?.ritardoAutomatico ?? true,
    note: anagrafica?.note ?? '',
  });
  const [attesa, setAttesa] = useState(false);

  const dettaglio = useCarico(
    () => (anagrafica ? api.get<Anagrafica>(`/api/anagrafiche/${anagrafica.id}`) : Promise.resolve(null)),
    [anagrafica?.id],
  );

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttesa(true);
    try {
      const corpo = {
        ...v,
        partitaIva: v.partitaIva || null,
        codiceFiscale: v.codiceFiscale || null,
        categoriaId: v.categoriaId || null,
        ritardoMedioGiorni: Number(v.ritardoMedioGiorni),
        ritardoAutomatico: v.ritardoAutomatico,
      };
      if (anagrafica) await api.put(`/api/anagrafiche/${anagrafica.id}`, corpo);
      else await api.post('/api/anagrafiche', corpo);
      avvisa(anagrafica ? 'Anagrafica aggiornata.' : 'Anagrafica creata.');
      onSalvata();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Salvataggio non riuscito.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  const elimina = async () => {
    if (!anagrafica || !confirm(`Eliminare ${anagrafica.denominazione}?`)) return;
    try {
      await api.del(`/api/anagrafiche/${anagrafica.id}`);
      avvisa('Anagrafica eliminata.');
      onSalvata();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Eliminazione non riuscita.', 'errore');
    }
  };

  const stat = dettaglio.dati?.statistichePagamento;

  return (
    <Dialogo
      aperto
      titolo={anagrafica ? anagrafica.denominazione : 'Nuova anagrafica'}
      onChiudi={onChiudi}
      larghezza="max-w-3xl"
    >
      <form onSubmit={salva} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etichetta="Denominazione">
            <input
              className="campo"
              required
              value={v.denominazione}
              onChange={(e) => setV({ ...v, denominazione: e.target.value })}
            />
          </Campo>
          <Campo etichetta="Tipo" aiuto="«Entrambi» serve quando la controparte è insieme cliente e fornitore.">
            <select className="campo" value={v.tipo} onChange={(e) => setV({ ...v, tipo: e.target.value as Anagrafica['tipo'] })}>
              <option value="CLIENTE">Cliente</option>
              <option value="FORNITORE">Fornitore</option>
              <option value="ENTRAMBI">Entrambi</option>
            </select>
          </Campo>
          <Campo etichetta="Partita IVA" aiuto="È la chiave con cui si abbinano le fatture elettroniche.">
            <input className="campo num" value={v.partitaIva} onChange={(e) => setV({ ...v, partitaIva: e.target.value })} />
          </Campo>
          <Campo etichetta="Codice fiscale">
            <input className="campo num" value={v.codiceFiscale} onChange={(e) => setV({ ...v, codiceFiscale: e.target.value })} />
          </Campo>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <Campo etichetta="Indirizzo" larghezza="sm:col-span-2">
            <input className="campo" value={v.indirizzo} onChange={(e) => setV({ ...v, indirizzo: e.target.value })} />
          </Campo>
          <Campo etichetta="CAP">
            <input className="campo" value={v.cap} onChange={(e) => setV({ ...v, cap: e.target.value })} />
          </Campo>
          <Campo etichetta="Provincia">
            <input className="campo" maxLength={4} value={v.provincia} onChange={(e) => setV({ ...v, provincia: e.target.value })} />
          </Campo>
          <Campo etichetta="Comune" larghezza="sm:col-span-2">
            <input className="campo" value={v.comune} onChange={(e) => setV({ ...v, comune: e.target.value })} />
          </Campo>
          <Campo etichetta="PEC" larghezza="sm:col-span-2">
            <input className="campo" value={v.pec} onChange={(e) => setV({ ...v, pec: e.target.value })} />
          </Campo>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etichetta="Voce del previsionale" aiuto="Su questa voce atterrano i documenti di questa controparte.">
            <select className="campo" value={v.categoriaId} onChange={(e) => setV({ ...v, categoriaId: e.target.value })}>
              <option value="">Nessuna</option>
              {piatto
                .filter((p) => !p.categoria.sistema)
                .map(({ categoria, livello }) => (
                  <option key={categoria.id} value={categoria.id}>
                    {' '.repeat(livello * 3)}
                    {categoria.nome} ({categoria.verso === 'ENTRATA' ? 'entrata' : 'uscita'})
                  </option>
                ))}
            </select>
          </Campo>
          {/* Il ritardo lo mantiene Mastro dai pagamenti osservati. Scriverlo
              a mano è legittimo — chi lo fa sa qualcosa che i dati non dicono
              ancora — ma allora l'automatismo si spegne, e va detto qui invece
              di lasciarlo scoprire quando il numero non si aggiorna più. */}
          <Campo
            etichetta="Ritardo sui pagamenti (giorni)"
            aiuto={
              v.ritardoAutomatico
                ? 'Lo mantiene Mastro, misurandolo a ogni riconciliazione. Se lo scrivi a mano resta quello che scrivi tu.'
                : stat && stat.pagate > 0
                  ? `Tenuto a mano. Sui ${stat.pagate} documenti già saldati risulterebbe ${stat.ritardo ?? 0} giorni.`
                  : 'Tenuto a mano: non ci sono ancora pagamenti su cui misurarlo.'
            }
          >
            <input
              type="number"
              className="campo num text-right"
              value={v.ritardoMedioGiorni}
              onChange={(e) =>
                setV({ ...v, ritardoMedioGiorni: Number(e.target.value), ritardoAutomatico: false })
              }
            />
            <label className="mt-1.5 flex items-center gap-2 text-xs text-inchiostro-600">
              <input
                type="checkbox"
                checked={v.ritardoAutomatico}
                onChange={(e) => setV({ ...v, ritardoAutomatico: e.target.checked })}
              />
              Lo mantiene Mastro dai pagamenti osservati
            </label>
          </Campo>
        </div>

        <Campo etichetta="Note">
          <textarea className="campo" rows={2} value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} />
        </Campo>

        <div className="flex justify-between gap-2 border-t border-inchiostro-200 pt-4">
          {anagrafica ? (
            <button type="button" className="bottone-pericolo" onClick={elimina}>
              Elimina
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" className="bottone-secondario" onClick={onChiudi}>
              Annulla
            </button>
            <button type="submit" className="bottone-primario" disabled={attesa}>
              Salva
            </button>
          </div>
        </div>
      </form>
    </Dialogo>
  );
}

interface RitardoOsservato {
  anagraficaId: string;
  denominazione: string;
  osservazioni: number;
  ritardoAttuale: number;
  ritardoOsservato: number;
  automatico: boolean;
  minimo: number;
  massimo: number;
}

const giorni = (g: number) => (g === 0 ? 'il giorno della scadenza' : g > 0 ? `${g} giorni dopo` : `${-g} giorni prima`);

/**
 * Le controparti in cui il ritardo memorizzato **non** coincide con quello
 * misurato sulle fatture già chiuse.
 *
 * Da quando l'automatismo esiste, questa scheda non è più il modo normale di
 * aggiornare il ritardo — Mastro lo fa da sé a ogni riconciliazione. Resta per
 * le controparti tenute a mano: sono le uniche che possono divergere, e vederle
 * accanto al dato misurato è il modo di accorgersi che una decisione presa mesi
 * fa non regge più.
 */
function RitardiOsservati({ onApplicato }: { onApplicato: () => void }) {
  const avvisa = useAvvisi();
  const [inCorso, setInCorso] = useState<string | null>(null);
  const proposte = useCarico(() => api.get<RitardoOsservato[]>('/api/anagrafiche/ritardi-osservati'), []);

  const applica = async (r: RitardoOsservato) => {
    setInCorso(r.anagraficaId);
    try {
      await api.put(`/api/anagrafiche/${r.anagraficaId}`, { ritardoMedioGiorni: r.ritardoOsservato });
      avvisa(`«${r.denominazione}»: le sue fatture aperte sono ora attese ${giorni(r.ritardoOsservato)}.`);
      proposte.ricarica();
      onApplicato();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Non è stato possibile applicare.', 'errore');
    } finally {
      setInCorso(null);
    }
  };

  if ((proposte.dati ?? []).length === 0) return null;

  return (
    <Scheda
      titolo="Ritardi tenuti a mano che non tornano"
      sottotitolo="Quanti giorni passano fra la scadenza e il giorno in cui il documento viene saldato del tutto, sulla mediana di almeno tre fatture chiuse. Applicandoli si spostano anche le fatture aperte di quella controparte."
      senzaPadding
    >
      <table className="dati">
        <thead>
          <tr>
            <th>Controparte</th>
            <th className="text-right">Pagamenti osservati</th>
            <th>Oggi Mastro assume</th>
            <th>Dai dati risulta</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {proposte.dati!.map((r) => (
            <tr key={r.anagraficaId}>
              <td className="font-medium">
                {r.denominazione}
                {!r.automatico && (
                  <span
                    className="ml-2 text-[10px] text-inchiostro-500"
                    title="Il ritardo è tenuto a mano: Mastro non lo aggiorna da solo."
                  >
                    a mano
                  </span>
                )}
              </td>
              <td className="num text-right">
                {r.osservazioni}
                <span className="ml-1 text-[10px] text-inchiostro-500" title="Il più presto e il più tardi osservati: se sono lontanissimi, la mediana regge ma val la pena guardare gli abbinamenti.">
                  (da {r.minimo} a {r.massimo}g)
                </span>
              </td>
              <td>{giorni(r.ritardoAttuale)}</td>
              <td className="font-medium">{giorni(r.ritardoOsservato)}</td>
              <td className="text-right">
                <button
                  className="bottone-secondario px-2 py-1 text-xs"
                  disabled={inCorso !== null}
                  onClick={() => applica(r)}
                >
                  Applica
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Scheda>
  );
}
